import { firestoreOperationsDb } from "../firebase";

export const TEST_REGISTRATION_MOBILE_POLICY_PATH = ["config", "testRegistrationMobilePolicy"];

export const DEFAULT_TEST_REGISTRATION_MOBILE_POLICY = Object.freeze({
  enabled: true,
  numbers: ["+61425715599", "+61406664034"],
  scopes: {
    patient: true,
    provider: true,
    careBuddy: false,
    workWithUs: false,
  },
});

const POLICY_CACHE_TTL_MS = 60 * 1000;
let policyCache = {
  expiresAt: 0,
  value: null,
};

export function normalizeAustralianMobileNumber(phone) {
  const raw = String(phone || "").trim();
  if (!raw) return "";

  const compact = raw.replace(/[^\d+]/g, "");
  if (!compact) return "";

  if (/^\+614\d{8}$/.test(compact)) return compact;

  const digits = compact.replace(/[^\d]/g, "");
  if (/^04\d{8}$/.test(digits)) return `+61${digits.slice(1)}`;
  if (/^614\d{8}$/.test(digits)) return `+${digits}`;

  return compact.startsWith("+") ? compact : digits;
}

export function sanitizeTestRegistrationMobilePolicy(data) {
  if (!data || typeof data !== "object") return null;

  const enabled = data.enabled === true;
  const rawNumbers = Array.isArray(data.numbers) ? data.numbers : [];
  const numbers = [...new Set(rawNumbers.map(normalizeAustralianMobileNumber).filter(Boolean))];
  const rawScopes = data.scopes && typeof data.scopes === "object" ? data.scopes : {};

  return {
    enabled,
    numbers,
    scopes: {
      patient: rawScopes.patient === true,
      provider: rawScopes.provider === true,
      careBuddy: rawScopes.careBuddy === true,
      workWithUs: rawScopes.workWithUs === true,
    },
  };
}

export async function readTestRegistrationMobilePolicy(db = firestoreOperationsDb, { forceRefresh = false } = {}) {
  if (!forceRefresh && policyCache.value && policyCache.expiresAt > Date.now()) {
    return policyCache.value;
  }

  try {
    const [collectionName, docId] = TEST_REGISTRATION_MOBILE_POLICY_PATH;
    const snap = await db.collection(collectionName).doc(docId).get();
    const policy = snap.exists ? sanitizeTestRegistrationMobilePolicy(snap.data()) : null;
    policyCache = {
      value: policy,
      expiresAt: Date.now() + POLICY_CACHE_TTL_MS,
    };
    return policy;
  } catch (error) {
    console.warn("[testRegistrationMobilePolicy] failed to read policy", error);
    policyCache = {
      value: null,
      expiresAt: Date.now() + POLICY_CACHE_TTL_MS,
    };
    return null;
  }
}

export async function isRegistrationMobileReuseAllowed(phoneNumber, scope, options = {}) {
  const normalizedPhone = normalizeAustralianMobileNumber(phoneNumber);
  if (!normalizedPhone || !scope) return false;

  const policy = await readTestRegistrationMobilePolicy(options.db, options);
  if (!policy || policy.enabled !== true) return false;
  if (!policy.scopes || policy.scopes[scope] !== true) return false;

  return policy.numbers.includes(normalizedPhone);
}

export function resetTestRegistrationMobilePolicyCache() {
  policyCache = {
    expiresAt: 0,
    value: null,
  };
}
